"use client";

import { useLocale } from "next-intl";
import { CommonModal } from "@/components/common/CommonModal";

type PromptsModalProps = {
    open: boolean;
    onClose: () => void;
};

export function PromptsModal({ open, onClose }: PromptsModalProps) {
    const locale = useLocale();
    const isKo = locale === "ko";

    return (
        <CommonModal open={open} onClose={onClose} ariaLabelledBy="prompts-modal-title">
            <div
                className="max-h-[80vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-5 text-slate-900 shadow-xl sm:p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between gap-2">
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400 sm:text-[13px]">
                            {isKo ? "카드 아이디어" : "Card ideas"}
                        </p>
                        <h2 id="prompts-modal-title" className="text-base font-semibold text-slate-900 sm:text-lg">
                            {isKo ? "스토리 카드 아이디어" : "Story card prompts"}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="focus-ring inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </header>

                <div className="prose prose-slate mt-4 max-w-none prose-p:leading-relaxed prose-p:text-[13px] prose-headings:text-sm sm:prose-p:text-sm sm:prose-headings:text-base">
                    <section className="mt-2">
                        <h3 className="font-semibold text-slate-900">
                            {isKo ? "1. 연말·새해 카드" : "1. Year-end and new year cards"}
                        </h3>
                        <ul className="mt-1 list-disc space-y-1 pl-5 text-[13px] sm:text-sm">
                            <li>
                                {isKo
                                    ? "올해 가장 잘한 한 가지와, 내년에 이어가고 싶은 한 가지를 정리해 보세요."
                                    : "Write one thing you did well this year and one thing you want to carry into next year."}
                            </li>
                            <li>
                                {isKo
                                    ? "올해를 한 단어로 표현한 뒤, 그 이유를 한 줄로 덧붙여 보세요."
                                    : "Describe this year in one word and add a short line explaining why."}
                            </li>
                            <li>
                                {isKo
                                    ? "내년의 나에게 보내는 짧은 편지를 한 문장으로 남겨 보세요."
                                    : "Leave a one-sentence letter to next year’s you."}
                            </li>
                        </ul>
                    </section>

                    <section className="mt-4">
                        <h3 className="font-semibold text-slate-900">
                            {isKo ? "2. 한 달을 정리하는 카드" : "2. Monthly recap cards"}
                        </h3>
                        <ul className="mt-1 list-disc space-y-1 pl-5 text-[13px] sm:text-sm">
                            <li>
                                {isKo
                                    ? "이번 달을 떠올리면 가장 먼저 생각나는 장면이나 기분을 한 줄로 적어 보세요."
                                    : "Write a single line about the first scene or feeling that comes to mind from this month."}
                            </li>
                            <li>
                                {isKo
                                    ? "이번 달에 새로 시작한 것, 혹은 끝낸 것을 카드로 기록해 보세요."
                                    : "Turn something you started or finished this month into a one-line card."}
                            </li>
                            <li>
                                {isKo
                                    ? "이번 달을 색으로 표현하고, 그 이유를 곁들여 적어 보세요."
                                    : "Pick a color for this month and explain why in one sentence."}
                            </li>
                        </ul>
                    </section>

                    <section className="mt-4">
                        <h3 className="font-semibold text-slate-900">
                            {isKo ? "3. 목표·습관 카드" : "3. Goals and habit cards"}
                        </h3>
                        <ul className="mt-1 list-disc space-y-1 pl-5 text-[13px] sm:text-sm">
                            <li>
                                {isKo
                                    ? "이번 주에 지키고 싶은 작은 습관 하나를 정하고, 그 습관을 들였을 때의 모습을 상상해 보세요."
                                    : "Choose a small habit for this week and imagine what it looks like if it sticks—write that image in one line."}
                            </li>
                            <li>
                                {isKo
                                    ? "오늘 지킨 약속(물 마시기, 운동, 공부 등)을 체크하듯 한 줄로 남겨 보세요."
                                    : "Record a promise you kept today (water, exercise, study, etc.) as a single checked-off line."}
                            </li>
                            <li>
                                {isKo
                                    ? "‘오늘의 나에게 칭찬하고 싶은 것’ 하나를 짧게 적어 보세요."
                                    : "Write one thing you want to praise yourself for today."}
                            </li>
                        </ul>
                    </section>

                    <section className="mt-4">
                        <h3 className="font-semibold text-slate-900">
                            {isKo ? "4. 장면을 기록하는 카드" : "4. Scene-based daily cards"}
                        </h3>
                        <ul className="mt-1 list-disc space-y-1 pl-5 text-[13px] sm:text-sm">
                            <li>
                                {isKo
                                    ? "오늘 스쳐 지나간 풍경 중 하나를 골라, 장면을 묘사하듯 적어 보세요."
                                    : "Pick one scene from today that passed by and describe it briefly."}
                            </li>
                            <li>
                                {isKo
                                    ? "오늘 들렀던 장소(카페, 도서관, 지하철 등)를 그곳의 소리·공기와 함께 한 줄로 남겨 보세요."
                                    : "Write about a place you visited today with a hint of its sounds or air."}
                            </li>
                            <li>
                                {isKo
                                    ? "집에 돌아오는 길에 했던 생각 중, 가장 오래 남는 한 줄을 카드로 만들어 보세요."
                                    : "Turn the thought that stayed with you on your way home into a one-line card."}
                            </li>
                        </ul>
                    </section>
                </div>
            </div>
        </CommonModal>
    );
}

